import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Save, Info, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  HeroCard, InsightCard, SectionHeader
} from "@/components/marketing/common";
import { MARKETING_COLORS } from "@/config/marketingColors";
import { MetaAdsIntegration } from "@/components/marketing/MetaAdsIntegration";

// ── Platform metadata ─────────────────────────────────────────────────────────

const PLATFORMS = [
  {
    key: "meta",
    title: "Meta (Facebook & Instagram)",
    description: "Rastreie visitantes e crie públicos para campanhas no Facebook e Instagram.",
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
    icon: () => (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    fields: [
      { name: "facebook_pixel_id", label: "Pixel ID", placeholder: "123456789012345", hint: "Número de 10–20 dígitos" },
    ],
  },
  {
    key: "google",
    title: "Google",
    description: "Analytics GA4 para métricas + Google Ads para conversões e remarketing.",
    color: "text-red-500",
    bgColor: "bg-red-500/10",
    icon: () => (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
    ),
    fields: [
      { name: "google_analytics_id",  label: "Google Analytics 4 (GA4)", placeholder: "G-XXXXXXXXXX",   hint: "Começa com G-" },
      { name: "google_ads_id",        label: "Google Ads Conversion ID",  placeholder: "AW-000000000",   hint: "Começa com AW-" },
      { name: "google_tag_manager_id", label: "Google Tag Manager",       placeholder: "GTM-XXXXXXX",    hint: "Começa com GTM-" },
    ],
  },
  {
    key: "tiktok",
    title: "TikTok",
    description: "Pixel TikTok para campanhas de vídeo e remarketing na Gen-Z.",
    color: "text-pink-500",
    bgColor: "bg-pink-500/10",
    icon: () => (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.73a4.85 4.85 0 01-1.01-.04z"/>
      </svg>
    ),
    fields: [
      { name: "tiktok_pixel_id", label: "TikTok Pixel ID", placeholder: "XXXXXXXXXXXXXXXXXX", hint: "16–24 caracteres alfanuméricos" },
    ],
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

const EMPTY = {
  facebook_pixel_id: "", google_analytics_id: "", google_ads_id: "",
  tiktok_pixel_id: "", google_tag_manager_id: "",
};

export function PixelsTab() {
  const { authAxios } = useAuth();
  const [form, setForm]       = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    authAxios.get("/restaurant/integrations")
      .then(r => setForm({ ...EMPTY, ...r.data }))
      .catch(() => toast.error("Erro ao carregar configurações"))
      .finally(() => setLoading(false));
  }, [authAxios]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authAxios.post("/restaurant/integrations", form);
      setForm({ ...EMPTY, ...res.data });
      toast.success("Configurações salvas com sucesso");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Erro ao salvar. Verifique os IDs.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
        <div className="animate-pulse">Carregando configurações...</div>
      </div>
    );
  }

  // Count connected pixels
  const connectedCount = [
    form.facebook_pixel_id,
    form.google_analytics_id,
    form.google_ads_id,
    form.tiktok_pixel_id,
  ].filter(Boolean).length;

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <HeroCard
        title="Pixels & Remarketing"
        metric={`${connectedCount}`}
        subMetrics={[
          {
            label: "Plataformas Conectadas",
            value: `${connectedCount}/3 configuradas`,
          },
        ]}
        color={MARKETING_COLORS.Pixels}
      />

      {/* Overview Banner */}
      <div className="flex gap-2 p-4 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900">
        <Info className="w-5 h-5 mt-0.5 shrink-0 text-blue-600" />
        <span>
          Configure seus pixels para ativar campanhas de remarketing automático. Os eventos são enviados quando visitantes abrem o menu, adicionam ao carrinho e finalizam pedidos.
        </span>
      </div>

      {/* Status Cards */}
      <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-4">
        {PLATFORMS.map(platform => {
          const isConfigured = platform.fields.some(f => form[f.name]);
          return (
            <div key={platform.key} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className={`${platform.bgColor} ${platform.color} p-2 rounded-md shrink-0`}>
                  <platform.icon />
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-foreground">{platform.title}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    {isConfigured ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                        <span className="text-xs text-emerald-700">Conectado</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
                        <span className="text-xs text-amber-700">Não configurado</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Configuration Section Header */}
      <SectionHeader
        title="Configuração de Pixels"
        subtitle="Insira os IDs das suas plataformas de advertising"
      />

      {/* Platform cards */}
      <div className="grid gap-6">
        {PLATFORMS.map(platform => (
          <Card key={platform.key} className="overflow-hidden">
            <CardHeader className="pb-3 bg-slate-50 border-b">
              <CardTitle className="flex items-center gap-2 text-base">
                <span className={`${platform.bgColor} ${platform.color} p-1.5 rounded-md`}>
                  <platform.icon />
                </span>
                {platform.title}
              </CardTitle>
              <CardDescription className="text-xs">{platform.description}</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {platform.fields.map(field => {
                const isConfigured = !!form[field.name];
                return (
                  <div key={field.name} className="space-y-1.5">
                    <Label htmlFor={field.name} className="flex items-center gap-2">
                      {field.label}
                      {isConfigured && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                    </Label>
                    <Input
                      id={field.name}
                      value={form[field.name] || ""}
                      onChange={e => setForm(prev => ({ ...prev, [field.name]: e.target.value }))}
                      placeholder={field.placeholder}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">{field.hint}</p>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Meta Ads Integration (v1) */}
      <div className="mt-8 pt-6 border-t">
        <SectionHeader
          title="Inteligência de Marketing"
          subtitle="Conecte suas campanhas Meta Ads"
        />
        <MetaAdsIntegration />
      </div>

      {/* Best Practices Insights */}
      <div className="mt-8 pt-6 border-t">
        <SectionHeader
          title="Boas Práticas"
          subtitle="Maximize o ROI do seu remarketing"
        />
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <InsightCard
            type="insight"
            title="Rastreamento de Eventos"
            message="Configure eventos personalizados no menu público para capturar visualizações, adições ao carrinho e compras."
          />
          <InsightCard
            type="success"
            title="Públicos Segmentados"
            message="Use os dados dos pixels para criar públicos personalizados e campanhas de remarketing no Meta e Google."
          />
          <InsightCard
            type="alert"
            title="Verificação CAPI"
            message="Após configurar, verifique que os eventos estão sendo recebidos corretamente no painel de cada plataforma."
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleSave} disabled={saving} size="lg" className="gap-2">
          <Save className="w-4 h-4" />
          {saving ? "Salvando…" : "Salvar configurações"}
        </Button>
      </div>
    </div>
  );
}
